import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("working"); // working | done | error
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-run guard: the token is single-use
    ran.current = true;
    const token = params.get("token") || "";
    if (!token) {
      setState("error");
      setError("This verification link is missing its token. Request a fresh one below.");
      return;
    }
    api.post("/auth/verify/email/confirm-token", { token })
      .then(async (res) => {
        if (res.data.token) localStorage.setItem("ht_token", res.data.token);
        await refreshUser().catch(() => {});
        setState("done");
        setTimeout(() => navigate(res.data.role === "admin" ? "/control-tower" : "/dashboard"), 1800);
      })
      .catch((err) => {
        setState("error");
        setError(formatApiError(err, "That verification link didn't work."));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resend = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/verify/email/resend", { email });
    } catch {
      /* response is intentionally generic either way */
    }
    setResendState("sent");
  };

  return (
    <main className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="verify-email-page">
      <Seo title="Verify your email | HiredToday" description="Confirm your email address to activate your HiredToday account." path="/verify-email" />
      {state === "working" && (
        <>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">Verifying…</h1>
          <p className="mt-3 text-sm text-zinc-500" data-testid="verify-email-working">Confirming your email address, one moment.</p>
        </>
      )}
      {state === "done" && (
        <div className="border border-green-200 bg-green-50 rounded-md px-5 py-6" data-testid="verify-email-success">
          <CheckCircle2 className="h-8 w-8 text-green-600 mb-3" />
          <h1 className="font-display text-2xl font-bold text-zinc-950">You're verified.</h1>
          <p className="mt-2 text-sm text-zinc-600">Your account is now active — taking you to your dashboard…</p>
        </div>
      )}
      {state === "error" && (
        <div data-testid="verify-email-error">
          <XCircle className="h-8 w-8 text-red-500 mb-3" />
          <h1 className="font-display text-2xl font-bold text-zinc-950">That link didn't work.</h1>
          <p className="mt-2 text-sm text-zinc-600">{error}</p>
          <div className="mt-6 space-y-3">
            <label className="text-sm font-medium text-zinc-700 block">Your email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="verify-email-resend-input"
              className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="button" onClick={resend} disabled={!email.trim() || resendState !== "idle"}
              data-testid="verify-email-resend-button"
              className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
              {resendState === "sent" ? "If that account needs verification, a fresh link is on its way" : resendState === "sending" ? "Sending…" : "Send a fresh verification link"}
            </button>
          </div>
        </div>
      )}
      <p className="mt-6 text-sm text-zinc-500 text-center">
        <Link to="/login" data-testid="verify-email-to-login" className="text-blue-700 font-medium">Back to log in</Link>
      </p>
    </main>
  );
}
